const DB_NAME = "abos-offline-pos";
const DB_VERSION = 1;

export const OFFLINE_STORES = Object.freeze({
  META: "meta",
  DEVICES: "devices",
  SESSIONS: "sessions",
  CATALOG_ITEMS: "catalog_items",
  BARCODES: "barcodes",
  INVENTORY: "inventory_state",
  DRAFT_CARTS: "draft_carts",
  OFFLINE_SALES: "offline_sales",
  OUTBOX: "outbox",
  SYNC_RESULTS: "sync_results",
});

let dbPromise = null;

export function offlineStorageSupported() {
  return typeof window !== "undefined" && "indexedDB" in window;
}

function requestPromise(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error("IndexedDB request failed."));
  });
}

function transactionPromise(transaction) {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onabort = () => reject(transaction.error || new Error("IndexedDB transaction aborted."));
    transaction.onerror = () => reject(transaction.error || new Error("IndexedDB transaction failed."));
  });
}

function createScopedStore(db, name, keyPath = "key") {
  const store = db.createObjectStore(name, { keyPath });
  if (keyPath !== "scope") store.createIndex("scope", "scope", { unique: false });
  return store;
}

export function openOfflineDatabase() {
  if (!offlineStorageSupported()) {
    return Promise.reject(new Error("This browser does not support IndexedDB."));
  }
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    const request = window.indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(OFFLINE_STORES.META)) {
        db.createObjectStore(OFFLINE_STORES.META, { keyPath: "key" });
      }
      if (!db.objectStoreNames.contains(OFFLINE_STORES.DEVICES)) {
        db.createObjectStore(OFFLINE_STORES.DEVICES, { keyPath: "scope" });
      }
      if (!db.objectStoreNames.contains(OFFLINE_STORES.SESSIONS)) {
        db.createObjectStore(OFFLINE_STORES.SESSIONS, { keyPath: "scope" });
      }
      if (!db.objectStoreNames.contains(OFFLINE_STORES.CATALOG_ITEMS)) {
        createScopedStore(db, OFFLINE_STORES.CATALOG_ITEMS);
      }
      if (!db.objectStoreNames.contains(OFFLINE_STORES.BARCODES)) {
        createScopedStore(db, OFFLINE_STORES.BARCODES);
      }
      if (!db.objectStoreNames.contains(OFFLINE_STORES.INVENTORY)) {
        createScopedStore(db, OFFLINE_STORES.INVENTORY);
      }
      if (!db.objectStoreNames.contains(OFFLINE_STORES.DRAFT_CARTS)) {
        db.createObjectStore(OFFLINE_STORES.DRAFT_CARTS, { keyPath: "scope" });
      }
      if (!db.objectStoreNames.contains(OFFLINE_STORES.OFFLINE_SALES)) {
        const store = db.createObjectStore(OFFLINE_STORES.OFFLINE_SALES, { keyPath: "offline_transaction_id" });
        store.createIndex("scope", "scope", { unique: false });
        store.createIndex("status", "status", { unique: false });
      }
      if (!db.objectStoreNames.contains(OFFLINE_STORES.OUTBOX)) {
        const store = db.createObjectStore(OFFLINE_STORES.OUTBOX, { keyPath: "offline_transaction_id" });
        store.createIndex("scope", "scope", { unique: false });
        store.createIndex("status", "status", { unique: false });
      }
      if (!db.objectStoreNames.contains(OFFLINE_STORES.SYNC_RESULTS)) {
        createScopedStore(db, OFFLINE_STORES.SYNC_RESULTS, "offline_transaction_id");
      }
    };
    request.onsuccess = () => {
      const db = request.result;
      db.onversionchange = () => {
        db.close();
        dbPromise = null;
      };
      resolve(db);
    };
    request.onerror = () => {
      dbPromise = null;
      reject(request.error || new Error("Could not open the Offline POS database."));
    };
    request.onblocked = () => {
      dbPromise = null;
      reject(new Error("Offline POS database upgrade is blocked by another ABOS tab."));
    };
  });

  return dbPromise;
}

export async function getOfflineRecord(storeName, key) {
  const db = await openOfflineDatabase();
  const tx = db.transaction(storeName, "readonly");
  return requestPromise(tx.objectStore(storeName).get(key));
}

export async function putOfflineRecord(storeName, value) {
  const db = await openOfflineDatabase();
  const tx = db.transaction(storeName, "readwrite");
  tx.objectStore(storeName).put(value);
  await transactionPromise(tx);
  return value;
}

export async function deleteOfflineRecord(storeName, key) {
  const db = await openOfflineDatabase();
  const tx = db.transaction(storeName, "readwrite");
  tx.objectStore(storeName).delete(key);
  await transactionPromise(tx);
}

export async function getOfflineRecordsByScope(storeName, scope) {
  const db = await openOfflineDatabase();
  const tx = db.transaction(storeName, "readonly");
  const store = tx.objectStore(storeName);
  if (!store.indexNames.contains("scope")) {
    throw new Error(`${storeName} is not a scoped Offline POS store.`);
  }
  return requestPromise(store.index("scope").getAll(scope));
}

export async function commitOfflineReadSnapshot({
  scope,
  metadata,
  catalogRecords,
  barcodeRecords,
  inventoryRecords,
}) {
  const db = await openOfflineDatabase();
  const tx = db.transaction(
    [
      OFFLINE_STORES.META,
      OFFLINE_STORES.CATALOG_ITEMS,
      OFFLINE_STORES.BARCODES,
      OFFLINE_STORES.INVENTORY,
    ],
    "readwrite",
  );

  const catalogStore = tx.objectStore(OFFLINE_STORES.CATALOG_ITEMS);
  const barcodeStore = tx.objectStore(OFFLINE_STORES.BARCODES);
  const inventoryStore = tx.objectStore(OFFLINE_STORES.INVENTORY);
  const metaStore = tx.objectStore(OFFLINE_STORES.META);

  for (const record of catalogRecords) catalogStore.put(record);
  for (const record of barcodeRecords) barcodeStore.put(record);
  for (const record of inventoryRecords) inventoryStore.put(record);
  metaStore.put({ ...metadata, key: `snapshot:${scope}` });

  await transactionPromise(tx);
  return metadata;
}

async function deleteScopedRecordsExceptSnapshot(storeName, scope, snapshotId) {
  const db = await openOfflineDatabase();
  const tx = db.transaction([OFFLINE_STORES.META, storeName], "readwrite");
  const done = transactionPromise(tx);
  const metaStore = tx.objectStore(OFFLINE_STORES.META);
  const currentMetadata = await requestPromise(metaStore.get(`snapshot:${scope}`));
  if (currentMetadata?.snapshot_id !== snapshotId) {
    await done;
    return;
  }

  const store = tx.objectStore(storeName);
  const index = store.index("scope");
  await new Promise((resolve, reject) => {
    const request = index.openCursor(scope);
    request.onerror = () => reject(
      request.error || new Error("Could not clean old Offline POS snapshot rows."),
    );
    request.onsuccess = () => {
      const cursor = request.result;
      if (!cursor) {
        resolve();
        return;
      }
      if (cursor.value?.snapshot_id !== snapshotId) cursor.delete();
      cursor.continue();
    };
  });
  await done;
}

export async function cleanupOldOfflineReadSnapshots(scope, snapshotId) {
  await Promise.all([
    deleteScopedRecordsExceptSnapshot(OFFLINE_STORES.CATALOG_ITEMS, scope, snapshotId),
    deleteScopedRecordsExceptSnapshot(OFFLINE_STORES.BARCODES, scope, snapshotId),
    deleteScopedRecordsExceptSnapshot(OFFLINE_STORES.INVENTORY, scope, snapshotId),
  ]);
}

export async function clearOfflineReadSnapshot(scope) {
  if (!scope) return;
  const db = await openOfflineDatabase();
  const storeNames = [
    OFFLINE_STORES.CATALOG_ITEMS,
    OFFLINE_STORES.BARCODES,
    OFFLINE_STORES.INVENTORY,
  ];

  for (const storeName of storeNames) {
    const tx = db.transaction(storeName, "readwrite");
    const store = tx.objectStore(storeName);
    const done = transactionPromise(tx);
    await new Promise((resolve, reject) => {
      const request = store.index("scope").openCursor(scope);
      request.onerror = () => reject(request.error || new Error("Could not clear Offline POS snapshot rows."));
      request.onsuccess = () => {
        const cursor = request.result;
        if (!cursor) {
          resolve();
          return;
        }
        cursor.delete();
        cursor.continue();
      };
    });
    await done;
  }

  await deleteOfflineRecord(OFFLINE_STORES.META, `snapshot:${scope}`);
}

export async function commitOfflineSaleTransaction({
  saleRecord,
  outboxRecord,
  inventoryDeductions,
}) {
  const db = await openOfflineDatabase();
  const tx = db.transaction(
    [OFFLINE_STORES.OFFLINE_SALES, OFFLINE_STORES.OUTBOX, OFFLINE_STORES.INVENTORY],
    "readwrite",
  );
  const done = transactionPromise(tx);
  const salesStore = tx.objectStore(OFFLINE_STORES.OFFLINE_SALES);
  const outboxStore = tx.objectStore(OFFLINE_STORES.OUTBOX);
  const inventoryStore = tx.objectStore(OFFLINE_STORES.INVENTORY);

  try {
    const existingSale = await requestPromise(
      salesStore.get(saleRecord.offline_transaction_id),
    );
    if (existingSale) {
      throw new Error("Offline transaction ID already exists on this device.");
    }

    const inventoryRequests = inventoryDeductions.map((deduction) => ({
      deduction,
      request: inventoryStore.get(deduction.key),
    }));
    const inventoryRows = await Promise.all(
      inventoryRequests.map(({ request }) => requestPromise(request)),
    );

    const updatedInventory = inventoryRequests.map(({ deduction }, index) => {
      const row = inventoryRows[index];
      if (!row || row.snapshot_id !== deduction.snapshot_id) {
        throw new Error("Offline inventory snapshot changed. Refresh the cart and try again.");
      }
      const physical = Number(row.quantity || 0);
      const reserved = Number(row.reserved_quantity || 0);
      const pending = Number(row.pending_offline_sold_quantity || 0);
      const available = Math.max(0, physical - reserved - pending);
      if (deduction.quantity > available) {
        throw new Error(
          `Only ${available} unit${available === 1 ? " is" : "s are"} available in the trusted offline snapshot.`,
        );
      }
      return {
        ...row,
        pending_offline_sold_quantity: pending + deduction.quantity,
      };
    });

    for (const row of updatedInventory) inventoryStore.put(row);
    salesStore.add(saleRecord);
    outboxStore.put(outboxRecord);
    await done;
    return saleRecord;
  } catch (error) {
    try { tx.abort(); } catch { /* transaction may already be closed */ }
    try { await done; } catch { /* preserve original error */ }
    throw error;
  }
}

export function selectOfflineOutboxBatch({
  records,
  sales = [],
  limit = 25,
  cashierUserPublicId = null,
  devicePublicId = null,
}) {
  const salesById = new Map(
    (Array.isArray(sales) ? sales : []).map((sale) => [sale.offline_transaction_id, sale]),
  );
  return (Array.isArray(records) ? records : [])
    // SYNCING is intentionally retryable after a tab/browser crash.  A new
    // runtime has no in-memory request to wait for, while backend idempotency
    // guarantees that replaying a committed transaction is safe.
    .filter((record) => ["PENDING", "SYNCING"].includes(record.status))
    .filter((record) => (
      !cashierUserPublicId || record.cashier_user_public_id === cashierUserPublicId
    ))
    .filter((record) => {
      if (!devicePublicId) return true;
      const boundDevicePublicId = record.device_public_id
        || salesById.get(record.offline_transaction_id)?.device_public_id
        || null;
      return boundDevicePublicId === devicePublicId;
    })
    .sort((left, right) => String(left.created_at).localeCompare(String(right.created_at)))
    .slice(0, Math.max(1, Number(limit) || 25));
}

export async function listOfflineOutboxByScope(
  scope,
  limit = 25,
  cashierUserPublicId = null,
  devicePublicId = null,
) {
  const records = await getOfflineRecordsByScope(OFFLINE_STORES.OUTBOX, scope);
  const sales = devicePublicId
    ? await getOfflineRecordsByScope(OFFLINE_STORES.OFFLINE_SALES, scope)
    : [];
  return selectOfflineOutboxBatch({
    records,
    sales,
    limit,
    cashierUserPublicId,
    devicePublicId,
  });
}

export async function markOfflineOutboxSyncing(scope, transactionIds) {
  if (!transactionIds.length) return;
  const db = await openOfflineDatabase();
  const tx = db.transaction(
    [OFFLINE_STORES.OUTBOX, OFFLINE_STORES.OFFLINE_SALES],
    "readwrite",
  );
  const outboxStore = tx.objectStore(OFFLINE_STORES.OUTBOX);
  const salesStore = tx.objectStore(OFFLINE_STORES.OFFLINE_SALES);
  // Queue both object-store reads before awaiting either set. This keeps the
  // IndexedDB transaction active consistently across browsers while we update
  // outbox + sale state atomically.
  const requests = transactionIds.map((id) => outboxStore.get(id));
  const saleRequests = transactionIds.map((id) => salesStore.get(id));
  const [records, sales] = await Promise.all([
    Promise.all(requests.map((request) => requestPromise(request))),
    Promise.all(saleRequests.map((request) => requestPromise(request))),
  ]);
  const now = new Date().toISOString();
  records.forEach((record, index) => {
    if (record?.scope === scope && ["PENDING", "SYNCING"].includes(record.status)) {
      outboxStore.put({
        ...record,
        status: "SYNCING",
        sync_attempts: Number(record.sync_attempts || 0) + 1,
        last_sync_attempt_at: now,
        last_sync_error: null,
      });
      const sale = sales[index];
      if (sale?.scope === scope && ["PENDING", "SYNCING"].includes(sale.status)) {
        salesStore.put({ ...sale, status: "SYNCING", updated_at: now });
      }
    }
  });
  await transactionPromise(tx);
}

export async function restoreOfflineOutboxPending(scope, transactionIds, errorMessage = null) {
  if (!transactionIds.length) return;
  const db = await openOfflineDatabase();
  const tx = db.transaction(
    [OFFLINE_STORES.OUTBOX, OFFLINE_STORES.OFFLINE_SALES],
    "readwrite",
  );
  const outboxStore = tx.objectStore(OFFLINE_STORES.OUTBOX);
  const salesStore = tx.objectStore(OFFLINE_STORES.OFFLINE_SALES);
  // Queue both object-store reads before awaiting either set. This keeps the
  // IndexedDB transaction active consistently across browsers while we update
  // outbox + sale state atomically.
  const requests = transactionIds.map((id) => outboxStore.get(id));
  const saleRequests = transactionIds.map((id) => salesStore.get(id));
  const [records, sales] = await Promise.all([
    Promise.all(requests.map((request) => requestPromise(request))),
    Promise.all(saleRequests.map((request) => requestPromise(request))),
  ]);
  const now = new Date().toISOString();
  records.forEach((record, index) => {
    if (record?.scope === scope && record.status === "SYNCING") {
      outboxStore.put({
        ...record,
        status: "PENDING",
        last_sync_error: errorMessage || null,
      });
      const sale = sales[index];
      if (sale?.scope === scope && sale.status === "SYNCING") {
        salesStore.put({
          ...sale,
          status: "PENDING",
          last_sync_error: errorMessage || null,
          updated_at: now,
        });
      }
    }
  });
  await transactionPromise(tx);
}

export async function commitOfflineSyncResults(scope, results) {
  if (!results.length) return;
  const db = await openOfflineDatabase();
  const tx = db.transaction(
    [OFFLINE_STORES.OFFLINE_SALES, OFFLINE_STORES.OUTBOX, OFFLINE_STORES.SYNC_RESULTS],
    "readwrite",
  );
  const salesStore = tx.objectStore(OFFLINE_STORES.OFFLINE_SALES);
  const outboxStore = tx.objectStore(OFFLINE_STORES.OUTBOX);
  const resultsStore = tx.objectStore(OFFLINE_STORES.SYNC_RESULTS);
  const saleRequests = results.map((result) => salesStore.get(result.offline_transaction_id));
  const sales = await Promise.all(saleRequests.map((request) => requestPromise(request)));
  const now = new Date().toISOString();

  results.forEach((result, index) => {
    const sale = sales[index];
    if (!sale || sale.scope !== scope) return;
    salesStore.put({
      ...sale,
      status: result.status,
      canonical_sale_public_id: result.sale?.sale_public_id || null,
      canonical_invoice_number: result.sale?.invoice_number || null,
      reconciliation: result.reconciliation || [],
      sync_error_code: result.error_code || null,
      sync_error_message: result.error_message || null,
      synced_at: result.sale ? now : sale.synced_at || null,
      updated_at: now,
    });
    outboxStore.delete(result.offline_transaction_id);
    resultsStore.put({
      ...result,
      scope,
      synced_at: now,
    });
  });
  await transactionPromise(tx);
}

export async function getPendingOfflineSoldQuantities(scope) {
  if (!scope) return new Map();
  const records = await getOfflineRecordsByScope(OFFLINE_STORES.OFFLINE_SALES, scope);
  return aggregateUnresolvedOfflineSoldQuantities(records);
}

export function aggregateUnresolvedOfflineSoldQuantities(records) {
  const totals = new Map();
  for (const record of records) {
    // FAILED_PERMANENTLY is deliberately included.  The physical sale already
    // happened, but the server could not represent it automatically.  Keeping
    // its local stock impact prevents a later snapshot refresh from making the
    // sold units appear available again while the failure awaits human review.
    if (!["PENDING", "SYNCING", "FAILED_PERMANENTLY"].includes(record.status)) continue;
    for (const item of record.transaction?.items || []) {
      const identity = item.identity_key
        || `${item.product_public_id || ""}::${item.product_variant_public_id || "simple"}`;
      if (!item.product_public_id) continue;
      totals.set(identity, (totals.get(identity) || 0) + Number(item.quantity || 0));
    }
  }
  return totals;
}

export async function getOfflineSaleRecordsByScope(scope) {
  if (!scope) return [];
  return getOfflineRecordsByScope(OFFLINE_STORES.OFFLINE_SALES, scope);
}
